import { Fragment, useEffect, useMemo, useRef } from "react";

import { useLocalSlice } from "@/Common/CustomHooks/useLocalSlice";
import { Icon } from "@/DesignSystem";
import { CareElement } from "@/Flowsheets/Models/CareElements/CareElement";

import styles from "./CareComponentTable.module.scss";

import ChevronLeft from "@/DesignSystem/Atoms/Icons/Types/ChevronLeft/chevron-left-regular.svg";
import ChevronRight from "@/DesignSystem/Atoms/Icons/Types/ChevronRight/chevron-right-regular.svg";

interface CareComponentTableProps {
    id?: string;
    columns: CareComponentTableColumn[];
    data: CareElement[];
}

interface IconWithKeyProps {
    children: JSX.Element;
    isVisible: boolean;
    id: string;
}

interface CareComponentTableState {
    truncatedColumns: CareComponentTableColumn[];
    fixedColumns: CareComponentTableColumn[];
    dataColumns: CareComponentTableColumn[];
    maxDataColumn: number | null;
}

type CareComponentTableColumn = {
    label: string;
    isFixed: boolean;
    render: (data: CareElement) => JSX.Element;
    getKey: (data: CareElement, index: number) => string;
};

const MAX_COLUMNS = 10;

function renderHeaders(columns: CareComponentTableColumn[], className: string): JSX.Element[] {
    return columns.map((column, i) => (
        <p
            key={`${column.label}_${i}`}
            className={`${styles.header} ${className}`}
        >
            {column.label}
        </p>
    ));
}

function renderBody(columns: CareComponentTableColumn[], data: CareElement[]): JSX.Element[] {
    return data.map(row => (
        <Fragment key={`row-${row.Name.trim()}`}>
            {columns.map((column, index) => (
                <div
                    key={column.getKey(row, index)}
                    className={`${styles.cell} care-element-col-${index}`}
                >
                    {column.render(row)}
                </div>
            ))}
        </Fragment>
    ));
}

function IconWithKey({ children, isVisible, id }: Readonly<IconWithKeyProps>): JSX.Element {
    return (
        <div className={styles.header} key={id}>
            <Icon size="sm">
                {isVisible && children}
            </Icon>
        </div>
    );
}

// Return type is too complexe to define explicitly
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function useCareComponentTableState(initialState: Readonly<CareComponentTableState>) {
    return useLocalSlice({
        initialState,
        reducers: {
            updateColumns: (state: CareComponentTableState, action: { payload: { columns: CareComponentTableColumn[] } }) => {
                state.fixedColumns = action.payload.columns.filter(column => column.isFixed);
                state.dataColumns = action.payload.columns.filter(column => !column.isFixed);

                const maxDataColumn = MAX_COLUMNS - state.fixedColumns.length;
                state.maxDataColumn = maxDataColumn;
                state.truncatedColumns = state.dataColumns.slice(0, maxDataColumn);
            },
            truncateColumns: (state: CareComponentTableState, action: { payload: { navigationIndex: number } }) => {
                state.truncatedColumns = state.dataColumns.slice(action.payload.navigationIndex, action.payload.navigationIndex + state.maxDataColumn);
            }
        } }
    );
}

function CareComponentTable({ id, columns, data }: Readonly<CareComponentTableProps>): JSX.Element {
    const [state, dispatch] = useCareComponentTableState({
        fixedColumns: [],
        dataColumns: [],
        maxDataColumn: null,
        truncatedColumns: []
    });

    const navigationIndex = useRef(0);

    useEffect(() => {
        navigationIndex.current = 0;
        dispatch.updateColumns({ columns });
    // Including disptach in the dependency array causes an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns]);

    const fixedHeaders = useMemo(() => renderHeaders(state.fixedColumns, styles.fixedColumn), [state.fixedColumns]);
    const headers = useMemo(() => renderHeaders(state.truncatedColumns, styles.dataColumn), [state.truncatedColumns]);
    const body = useMemo(() => renderBody(
        [
            ...state.fixedColumns,
            {
                label: "", // Empty for the chevron left column
                isFixed: true,
                render: () => <span />,
                getKey: row => `${row.Name.trim()}-chevron-left`
            },
            ...state.truncatedColumns
        ],
        data
    ), [state.fixedColumns, state.truncatedColumns, data]);

    function handleNavigation(increment: number): void {
        const newIndex = navigationIndex.current + increment;
        if (newIndex < 0 || newIndex + state.maxDataColumn > state.dataColumns.length) {
            return;
        }

        navigationIndex.current = newIndex;
        dispatch.truncateColumns({ navigationIndex: newIndex });
    }

    return (
        <div className={styles.careComponentTable}>
            {fixedHeaders}
            <IconWithKey
                id={`${id}-chevron-left`}
                isVisible={navigationIndex.current > 0 &&
                    state.dataColumns.length > (state.maxDataColumn ?? 0)
                }
            >
                <ChevronLeft
                    className={styles.chevron}
                    onClick={() => handleNavigation(-1)}
                />
            </IconWithKey>
            {headers}
            <IconWithKey id={`${id}-chevron-right`} isVisible={state.maxDataColumn + navigationIndex.current < state.dataColumns.length}>
                <ChevronRight
                    className={styles.chevron}
                    onClick={() => handleNavigation(1)}
                />
            </IconWithKey>
            {body}
        </div>
    );
}

export { CareComponentTable, CareComponentTableColumn };
